import React from 'react';
import { Box, Unstable_Grid2 as Grid, useTheme } from '@mui/material';
import dayjs from 'dayjs';

import { DatePicker, Select, Text, TextField } from 'components';
import { formatDate } from 'core/shared';
import { useToast } from 'core/toast/hooks';
import { useHasMobileView } from 'hooks/useHasMobileView';
import { replaceText, useTranslations } from 'hooks/useTranslations';

import { CustomString } from 'config/translations';
import { UseMutation, UseQuery } from 'useQuery';
import { InvoicingContract } from 'useQuery/types';

import {
  NONE_VAT,
  SectionSaveFooter,
  useDefaultVatId,
  useFrequencyMode,
  useVatOptions,
} from './InvoicingShared';

interface OrganisationInvoicingContractProps {
  organisationId: string;
}

type TermMode = 'open' | 'fixed';

// Preset subscription plans (hardcoded front-end for now); a customer's contract
// can also carry a bespoke name via the "Custom name…" option.
const SUBSCRIPTION_PRESETS = ['Figo Starter', 'Figo Growth', 'Figo Enterprise'];
const CUSTOM_PLAN = '__custom';

// DatePicker emits a UTC ISO string; format it back to the local calendar day so
// picking 01-08 doesn't roll back to 31-07 in timezones ahead of UTC.
function toBackendDate(value: string | null): string | null {
  if (!value) {
    return null;
  }
  return formatDate({ date: value, variant: 'backendShort' });
}

function OrganisationInvoicingContract({ organisationId }: OrganisationInvoicingContractProps) {
  const theme = useTheme();
  const { hasMobileView } = useHasMobileView();
  const translations = useTranslations('organisationInvoicing');
  const { addToast } = useToast();

  const { data: contract, refetch } = UseQuery.invoicingContract({ id: organisationId });
  const { mutateAsync: updateContract, isLoading } = UseMutation.updateInvoicingContract();
  const vatOptions = useVatOptions();
  const defaultVatId = useDefaultVatId();

  const [contractStart, setContractStart] = React.useState('');
  const [billingMonths, setBillingMonths] = React.useState(1);
  const [termMode, setTermMode] = React.useState<TermMode>('open');
  const [termCycles, setTermCycles] = React.useState(1);
  const [subscriptionPlan, setSubscriptionPlan] = React.useState<string>(CUSTOM_PLAN);
  const [customName, setCustomName] = React.useState('');
  const [subscriptionPrice, setSubscriptionPrice] = React.useState(0); // euros
  const [discount, setDiscount] = React.useState(0); // euros
  const [vatId, setVatId] = React.useState<string>('');

  function hydrate(data: InvoicingContract | null | undefined) {
    setContractStart(data?.contract_start ?? '');
    setBillingMonths(data?.billing_frequency_months ?? 1);
    if (data?.contract_term_cycles) {
      setTermMode('fixed');
      setTermCycles(data.contract_term_cycles);
    } else {
      setTermMode('open');
      setTermCycles(1);
    }
    const name = data?.subscription_name ?? '';
    if (name && SUBSCRIPTION_PRESETS.includes(name)) {
      setSubscriptionPlan(name);
      setCustomName('');
    } else {
      // Empty or bespoke name → custom option (empty name saves as null).
      setSubscriptionPlan(CUSTOM_PLAN);
      setCustomName(name);
    }
    setSubscriptionPrice((data?.subscription_net_price ?? 0) / 100);
    setDiscount((data?.discount_net ?? 0) / 100);
    setVatId(data?.vat_id ?? '');
  }

  React.useEffect(() => {
    hydrate(contract);
  }, [contract]);

  const effectiveName = subscriptionPlan === CUSTOM_PLAN ? customName.trim() : subscriptionPlan;

  // An organisation without a contract yet starts on the high rate; '' means the
  // field was never touched, so a saved contract keeps its own value, including
  // an explicit "no VAT". The preselection is not a pending change on its own.
  const vatPrefilled = !contract && vatId === '';
  const effectiveVatId = vatPrefilled ? defaultVatId : vatId || NONE_VAT;

  // Section-owned fields only. invoicing_active belongs to the master bar;
  // contract_end / subscription_billed_in_advance are derived server-side.
  const body = {
    contract_start: toBackendDate(contractStart),
    billing_frequency_months: billingMonths,
    contract_term_cycles: termMode === 'fixed' ? Math.max(1, termCycles) : null,
    subscription_name: effectiveName || null,
    subscription_net_price: Math.round(subscriptionPrice * 100),
    discount_net: Math.round(discount * 100),
    vat_id: effectiveVatId !== NONE_VAT ? effectiveVatId : null,
  };

  const hasChanges =
    body.contract_start !== (contract?.contract_start ?? null) ||
    body.billing_frequency_months !== (contract?.billing_frequency_months ?? 1) ||
    body.contract_term_cycles !== (contract?.contract_term_cycles ?? null) ||
    body.subscription_name !== (contract?.subscription_name ?? null) ||
    body.subscription_net_price !== (contract?.subscription_net_price ?? 0) ||
    body.discount_net !== (contract?.discount_net ?? 0) ||
    (!vatPrefilled && body.vat_id !== (contract?.vat_id ?? null));

  async function save() {
    try {
      await updateContract({ id: organisationId, contract: body });
      await refetch();
      addToast({ title: translations.contractSaved, severity: 'success' });
    } catch (error) {
      // handled by the mutation's onError toast
    }
  }

  const subscriptionOptions = [
    ...SUBSCRIPTION_PRESETS.map((preset) => ({ id: preset, label: preset as CustomString })),
    { id: CUSTOM_PLAN, label: translations.subscriptionCustomOption },
  ];

  // Billing frequency: Monthly / Yearly / Custom N months. Custom keeps the last
  // bespoke value, defaulting to 3 (quarterly) when switching over from 1/12.
  const { mode: frequencyMode, setIsCustom } = useFrequencyMode(billingMonths);

  function onFrequencyModeChange(value: string) {
    if (value === 'custom') {
      setIsCustom(true);
      setBillingMonths(billingMonths === 1 || billingMonths === 12 ? 3 : billingMonths);
      return;
    }
    setIsCustom(false);
    setBillingMonths(Number(value));
  }

  // ---- derived, live within-section hints -------------------------------

  // 1 month = invoiced afterwards (legacy behaviour); longer cycles in advance.
  const timingHint = billingMonths > 1 ? translations.timingAdvance : translations.timingAfterwards;

  function termUnitLabel(): CustomString {
    const single = Math.max(1, termCycles) === 1;
    if (billingMonths === 1) {
      return single ? translations.unitMonth : translations.unitMonths;
    }
    if (billingMonths === 12) {
      return single ? translations.unitYear : translations.unitYears;
    }
    return single ? translations.unitCycle : translations.unitCycles;
  }

  const derivedContractEnd =
    termMode === 'fixed' && contractStart && dayjs(contractStart).isValid()
      ? dayjs(contractStart)
          .startOf('month')
          .add(Math.max(1, termCycles) * billingMonths, 'month')
          .subtract(1, 'day')
      : null;

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        padding: 2,
        marginBottom: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        maxWidth: `calc(100vw - ${theme.spacing(hasMobileView ? 4 : 6)})`,
      }}
    >
      <Text.Bold>{translations.subscriptionTitle}</Text.Bold>

      <Grid container spacing={2} sx={{ paddingTop: 2 }}>
        <Grid xs={12} md={6}>
          <DatePicker
            label={translations.contractStart}
            value={contractStart}
            onChange={setContractStart}
            views={['month', 'year']}
            fullWidth
          />
        </Grid>

        {/* Billing frequency | Invoice every (N months). The 7+7 column overflow
            keeps each dropdown on its own row while widening it beyond a half.
            The timing note sits under the frequency it describes. */}
        <Grid xs={12} md={7}>
          <Select
            mode="single"
            label={translations.billingFrequency}
            value={frequencyMode}
            options={[
              { id: '1', label: translations.monthly },
              { id: '12', label: translations.yearly },
              { id: 'custom', label: translations.custom },
            ]}
            onChange={onFrequencyModeChange}
            fullWidth
          />
          <Box sx={{ paddingTop: 0.5 }}>
            <Text.Small color="gray">{timingHint}</Text.Small>
          </Box>
        </Grid>
        {frequencyMode === 'custom' && (
          <Grid xs={12} md={5}>
            <TextField
              type="number"
              label={translations.customMonths}
              value={billingMonths}
              onChange={(value) => setBillingMonths(Math.max(1, Math.round(value)))}
              fullWidth
            />
          </Grid>
        )}

        {/* Contract length | Ends after. The derived contract-end note sits under
            the contract length it depends on. */}
        <Grid xs={12} md={7}>
          <Select
            mode="single"
            label={translations.contractLength}
            value={termMode}
            options={[
              { id: 'open', label: translations.untilCancelled },
              { id: 'fixed', label: translations.fixedTerm },
            ]}
            onChange={setTermMode}
            fullWidth
          />
          {termMode === 'fixed' && !!derivedContractEnd && (
            <Box sx={{ paddingTop: 0.5 }}>
              <Text.Small color="gray">
                {replaceText({
                  text: translations.contractEnds,
                  replaceValues: [
                    formatDate({ date: derivedContractEnd, variant: 'dayMonthYear' }),
                  ],
                })}
              </Text.Small>
            </Box>
          )}
        </Grid>
        {termMode === 'fixed' && (
          <Grid xs={12} md={5}>
            {/* Half-width input leaves room for the unit to sit beside it,
                rather than wrapping awkwardly onto the line below. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 110 }}>
                <TextField
                  type="number"
                  label={translations.endsAfter}
                  value={termCycles}
                  onChange={(value) => setTermCycles(Math.max(1, Math.round(value)))}
                  fullWidth
                />
              </Box>
              <Text color="gray">{termUnitLabel()}</Text>
            </Box>
          </Grid>
        )}

        {/* Subscription | Custom subscription name */}
        <Grid xs={12}>
          <Select
            mode="single"
            label={translations.subscriptionName}
            value={subscriptionPlan}
            options={subscriptionOptions}
            onChange={setSubscriptionPlan}
            fullWidth
          />
        </Grid>
        {subscriptionPlan === CUSTOM_PLAN && (
          <Grid xs={12}>
            <TextField
              type="text"
              label={translations.customSubscriptionName}
              value={customName}
              onChange={setCustomName}
              placeholder={translations.customSubscriptionPlaceholder}
              fullWidth
            />
          </Grid>
        )}

        {/* Monthly price | VAT */}
        <Grid xs={12} md={6}>
          <TextField
            type="number"
            label={translations.subscriptionPrice}
            value={subscriptionPrice}
            onChange={setSubscriptionPrice}
            adornment="EUR"
            fullWidth
          />
        </Grid>
        <Grid xs={12} md={6}>
          <Select
            mode="single"
            label={translations.vat}
            value={effectiveVatId}
            options={vatOptions}
            onChange={setVatId}
            fullWidth
          />
        </Grid>

        {/* Monthly discount, spanning the full width */}
        <Grid xs={12}>
          <TextField
            type="number"
            label={translations.discount}
            value={discount}
            onChange={setDiscount}
            adornment="EUR"
            fullWidth
          />
        </Grid>
      </Grid>

      <SectionSaveFooter dirty={hasChanges} saving={isLoading} onSave={save} />
    </Box>
  );
}

export { OrganisationInvoicingContract };
